import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { MwHeader } from "@/components/mediaweb/MwHeader";
import { MwFooter } from "@/components/mediaweb/MwFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  project_type: "pwa-creation",
  message: "",
  company: "", // honeypot anti-bot, restera vide
};

export default function ContactPage() {
  const [form, setForm] = useState(initialForm);
  const [state, setState] = useState({ status: "idle", error: null });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "submitting", error: null });
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg =
          res.status === 429
            ? "Trop de demandes envoyées. Réessayez dans quelques minutes."
            : data.detail || "Une erreur est survenue, merci de réessayer.";
        setState({ status: "error", error: msg });
        return;
      }
      setState({ status: "success", error: null });
      setForm(initialForm);
    } catch (err) {
      setState({
        status: "error",
        error: "Connexion impossible. Vérifiez votre internet ou réessayez plus tard.",
      });
    }
  };

  return (
    <div className="mw-app">
      <MwHeader />

      <section className="mw-section mw-section-contact">
        <div className="mw-section-head">
          <span className="mw-section-eyebrow">Nous contacter</span>
          <h1 className="mw-h2">Parlons de votre projet</h1>
          <p className="mw-section-lead">
            Vous souhaitez créer votre propre radio internet ou en savoir plus
            sur nos services ? Détaillez-nous votre projet : type de radio,
            contenu envisagé, public cible et objectifs. Chaque demande est
            étudiée avec attention.
          </p>
        </div>

        <div className="mw-contact-grid">
          <div className="mw-contact-info">
            <div className="mw-contact-block">
              <span className="mw-contact-icon" aria-hidden="true">
                <Mail size={18} />
              </span>
              <div>
                <div className="mw-contact-title">E-mail</div>
                <a
                  href="mailto:creativmusicgroup@gmail.com"
                  className="mw-link"
                  data-testid="contact-email"
                >
                  creativmusicgroup@gmail.com
                </a>
              </div>
            </div>

            <div className="mw-contact-block">
              <span className="mw-contact-icon" aria-hidden="true">
                <Phone size={18} />
              </span>
              <div>
                <div className="mw-contact-title">Téléphone</div>
                <div className="mw-contact-muted">
                  Numéro transmis après un premier échange par e-mail.
                </div>
              </div>
            </div>

            <div className="mw-contact-block">
              <span className="mw-contact-icon" aria-hidden="true">
                <MapPin size={18} />
              </span>
              <div>
                <div className="mw-contact-title">Adresse</div>
                <div className="mw-contact-muted">
                  1 Poulton Close — 1st Floor<br />
                  Dover (Kent) CT17 0HL — England
                </div>
              </div>
            </div>

            <p className="mw-contact-note">
              Communication possible en français, anglais et italien. Nous vous
              répondons sous 48 h ouvrées.
            </p>
          </div>

          {state.status === "success" ? (
            <div className="mw-form mw-form-success" data-testid="contact-success">
              <CheckCircle2 size={40} className="mw-form-success-icon" />
              <h2 className="mw-form-success-title">Merci, votre demande est partie 👋</h2>
              <p className="mw-form-success-text">
                Nous avons bien reçu votre message. Un accusé de réception vient
                de vous être envoyé par e-mail. Notre équipe vous répondra sous
                48 h ouvrées.
              </p>
              <div className="mw-hero-cta">
                <button
                  type="button"
                  className="mw-btn mw-btn-ghost"
                  onClick={() => setState({ status: "idle", error: null })}
                  data-testid="contact-send-another"
                >
                  Envoyer une autre demande
                </button>
                <Link to="/" className="mw-btn mw-btn-primary">
                  Retour à l'accueil <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          ) : (
            <form className="mw-form" data-testid="contact-form" onSubmit={onSubmit}>
              <div className="mw-form-row">
                <label className="mw-label">
                  Nom complet
                  <input
                    type="text"
                    required
                    minLength={2}
                    maxLength={120}
                    placeholder="Jean Dupont"
                    className="mw-input"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-input-name"
                    disabled={state.status === "submitting"}
                  />
                </label>
                <label className="mw-label">
                  E-mail
                  <input
                    type="email"
                    required
                    placeholder="jean@exemple.com"
                    className="mw-input"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-input-email"
                    disabled={state.status === "submitting"}
                  />
                </label>
              </div>

              <label className="mw-label">
                Type de projet
                <select
                  className="mw-input"
                  value={form.project_type}
                  onChange={update("project_type")}
                  data-testid="contact-input-type"
                  disabled={state.status === "submitting"}
                >
                  <option value="pwa-creation">Créer une PWA radio professionnelle</option>
                  <option value="wall-stream">Diffuser ma radio sur WallTunes</option>
                  <option value="other">Autre demande / Renseignements</option>
                </select>
              </label>

              <label className="mw-label">
                Décrivez votre projet
                <textarea
                  rows="6"
                  required
                  minLength={8}
                  maxLength={4000}
                  placeholder="Type de radio, contenu envisagé, public cible, objectifs…"
                  className="mw-input mw-textarea"
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                  disabled={state.status === "submitting"}
                />
              </label>

              {/* Honeypot — caché aux humains, attire les bots */}
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                value={form.company}
                onChange={update("company")}
                className="mw-honeypot"
                aria-hidden="true"
              />

              {state.error && (
                <div className="mw-form-error" data-testid="contact-error" role="alert">
                  {state.error}
                </div>
              )}

              <button
                type="submit"
                className="mw-btn mw-btn-primary mw-form-submit"
                data-testid="contact-submit"
                disabled={state.status === "submitting"}
              >
                {state.status === "submitting" ? (
                  <>
                    <Loader2 size={16} className="mw-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    Envoyer ma demande <ArrowUpRight size={16} />
                  </>
                )}
              </button>
              <p className="mw-form-note">
                En soumettant, vous acceptez d'être recontacté à l'adresse e-mail
                renseignée. Aucune donnée n'est partagée avec des tiers.
              </p>
            </form>
          )}
        </div>
      </section>

      <MwFooter />
    </div>
  );
}
